import { clearAuthSession } from "utils/authSession";
import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useMaterialUIController, setDarkMode, setMiniSidenav } from "context";
import {
  LuUser,
  LuSettings,
  LuSun,
  LuMoon,
  LuLogOut,
  LuX,
  LuMenu,
  LuRefreshCw,
} from "react-icons/lu";
import "./AppHeader.css";
import { agreementRequest } from "utils/rctiAgreementAPI";
import { appConstants } from "utils/appConstants";
import { getCachedRequest } from "utils/requestCache";

// Exact vector icons from Figma Node 429:22151 (Topbar Component)
function SearchIcon(props) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M14 14L11.1068 11.1068M12.6667 7.33333C12.6667 10.2789 10.2789 12.6667 7.33333 12.6667C4.38781 12.6667 2 10.2789 2 7.33333C2 4.38781 4.38781 2 7.33333 2C10.2789 2 12.6667 4.38781 12.6667 7.33333Z"
        stroke="currentColor"
        strokeWidth="1.33"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CoinsIcon(props) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M9.16272 11.8241C8.96259 12.4858 8.59436 13.0844 8.09396 13.5614C7.59357 14.0384 6.97808 14.3776 6.30753 14.5459C5.63698 14.7141 4.93427 14.7057 4.26794 14.5215C3.60161 14.3372 2.99441 13.9834 2.50556 13.4946C2.01671 13.0057 1.66291 12.3985 1.47866 11.7322C1.29441 11.0658 1.286 10.3631 1.45427 9.69259C1.62253 9.02204 1.96171 8.40655 2.43873 7.90615C2.91575 7.40576 3.51431 7.03753 4.17605 6.8374M10 4H10.6667V6.66667M4.08936 9.84538L4.66669 9.51204L6.00002 11.8214M14.6667 5.33333C14.6667 7.54247 12.8758 9.33333 10.6667 9.33333C8.45753 9.33333 6.66667 7.54247 6.66667 5.33333C6.66667 3.12419 8.45753 1.33333 10.6667 1.33333C12.8758 1.33333 14.6667 3.12419 14.6667 5.33333Z"
        stroke="currentColor"
        strokeWidth="1.33"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InfoIcon(props) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M10 13.3333V10M10 6.66667H10.0083M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10C1.66667 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z"
        stroke="currentColor"
        strokeWidth="1.67"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BellIcon(props) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M8.55672 17.5C8.70301 17.7533 8.9134 17.9637 9.16676 18.11C9.42012 18.2563 9.70751 18.3333 10.0001 18.3333C10.2926 18.3333 10.58 18.2563 10.8334 18.11C11.0867 17.9637 11.2971 17.7533 11.4434 17.5M2.71833 12.7717C2.60947 12.891 2.53763 13.0394 2.51155 13.1988C2.48547 13.3582 2.50627 13.5217 2.57142 13.6695C2.63658 13.8173 2.74328 13.943 2.87855 14.0312C3.01381 14.1195 3.17182 14.1665 3.33333 14.1667H16.6667C16.8282 14.1667 16.9862 14.1199 17.1216 14.0318C17.2569 13.9437 17.3637 13.8181 17.4291 13.6704C17.4944 13.5227 17.5154 13.3592 17.4895 13.1998C17.4637 13.0404 17.392 12.892 17.2833 12.7725C16.175 11.63 15 10.4158 15 6.66667C15 5.34058 14.4732 4.06881 13.5355 3.13113C12.5979 2.19345 11.3261 1.66667 10 1.66667C8.67392 1.66667 7.40215 2.19345 6.46447 3.13113C5.52679 4.06881 5 5.34058 5 6.66667C5 10.4158 3.82417 11.63 2.71833 12.7717Z"
        stroke="currentColor"
        strokeWidth="1.67"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronDownIcon(props) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M4 6L8 10L12 6"
        stroke="currentColor"
        strokeWidth="1.67"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const parseSessionJSON = (key, fallback) => {
  const value = sessionStorage.getItem(key);
  if (!value || value === "undefined" || value === "null") return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const getInitials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "IR";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export default function AppHeader({
  onOpenNav,
  searchPlaceholder = "Search invoices, quotes, contacts",
  credits: creditsProp,
  userName: userNameProp,
  profileImage: profileImageProp,
  notificationCount = 0,
  onNotificationClick,
  onHelpClick,
  className = "",
  showSearch = true,
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [controller, controllerDispatch] = useMaterialUIController();
  const darkMode = Boolean(controller?.darkMode);

  const [agreementNotifications, setAgreementNotifications] = useState({
    count: 0,
  });
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [activeResult, setActiveResult] = useState(-1);
  const [searchRetry, setSearchRetry] = useState(0);

  const profileControlRef = useRef(null);
  const profileMenuRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);

  // Read session fallback data with reactive updates
  const [sessionUser, setSessionUser] = useState(() =>
    parseSessionJSON("LOGIN_DETAILS", {})
  );
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const handleProfileUpdate = () => {
      setSessionUser(parseSessionJSON("LOGIN_DETAILS", {}));
      setImageError(false);
    };
    window.addEventListener("user-profile-updated", handleProfileUpdate);
    window.addEventListener("storage", handleProfileUpdate);
    return () => {
      window.removeEventListener("user-profile-updated", handleProfileUpdate);
      window.removeEventListener("storage", handleProfileUpdate);
    };
  }, []);

  const [isSidebarClosed, setIsSidebarClosed] = useState(() => {
    return (
      Boolean(controller?.miniSidenav) ||
      (typeof window !== "undefined" &&
        window.innerWidth > 1024 &&
        sessionStorage.getItem("DESKTOP_SIDEBAR_COLLAPSED") === "true")
    );
  });

  useEffect(() => {
    setIsSidebarClosed(Boolean(controller?.miniSidenav));
  }, [controller?.miniSidenav]);

  useEffect(() => {
    const handleToggle = (e) => {
      if (e?.detail?.collapsed !== undefined) {
        setIsSidebarClosed(Boolean(e.detail.collapsed));
      } else {
        setIsSidebarClosed(
          document.body.classList.contains("sidebar-collapsed")
        );
      }
    };
    window.addEventListener("desktop-sidebar-toggled", handleToggle);
    return () => {
      window.removeEventListener("desktop-sidebar-toggled", handleToggle);
    };
  }, []);

  useEffect(() => {
    if (window.innerWidth > 1024) {
      document.body.classList.toggle("sidebar-collapsed", isSidebarClosed);
    }
  }, [isSidebarClosed]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024) {
        document.body.classList.remove("sidebar-collapsed");
      } else if (isSidebarClosed) {
        document.body.classList.add("sidebar-collapsed");
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarClosed]);

  const handleOpenDesktopSidebar = () => {
    setMiniSidenav(controllerDispatch, false);
    setIsSidebarClosed(false);
    document.body.classList.remove("sidebar-collapsed");
    try {
      sessionStorage.setItem("DESKTOP_SIDEBAR_COLLAPSED", "false");
    } catch {}
    window.dispatchEvent(
      new CustomEvent("desktop-sidebar-toggled", {
        detail: { collapsed: false },
      })
    );
    window.dispatchEvent(new CustomEvent("open-dashboard-navigation"));
    if (window.innerWidth <= 1024 && onOpenNav) onOpenNav();
  };

  const user = sessionUser;
  useEffect(() => {
    if (!sessionStorage.getItem("TOKEN") || !user.businessUserID)
      return undefined;
    let active = true;
    const refresh = () =>
      getCachedRequest(
        `agreement-notifications:${user.businessUserID}`,
        () => agreementRequest("/notifications", undefined, "get"),
        55000
      )
        .then((result) => {
          if (active) setAgreementNotifications(result);
        })
        .catch(() => {});
    refresh();
    const timer = setInterval(refresh, 60000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [user.businessUserID]);

  const userName =
    userNameProp ||
    user.fullName ||
    `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
    user.name ||
    user.companyName ||
    user.businessName ||
    "User";

  const profileImage = useMemo(() => {
    if (
      profileImageProp &&
      typeof profileImageProp === "string" &&
      profileImageProp.trim()
    ) {
      const trimmed = profileImageProp.trim();
      if (trimmed === "null" || trimmed === "undefined" || trimmed === "empty")
        return null;
      if (
        trimmed.startsWith("http://") ||
        trimmed.startsWith("https://") ||
        trimmed.startsWith("data:") ||
        trimmed.startsWith("blob:")
      ) {
        return trimmed;
      }
      return `${
        appConstants.baseURL
      }user/viewFile?fileName=${encodeURIComponent(trimmed)}`;
    }
    const raw =
      user.profilePictureFileName ||
      user.profilePicFileName ||
      user.profileImage ||
      user.avatar ||
      user.photo;
    if (!raw || typeof raw !== "string" || !raw.trim()) return null;
    const trimmed = raw.trim();
    if (trimmed === "null" || trimmed === "undefined" || trimmed === "empty")
      return null;
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("data:") ||
      trimmed.startsWith("blob:")
    ) {
      return trimmed;
    }
    return `${appConstants.baseURL}user/viewFile?fileName=${encodeURIComponent(
      trimmed
    )}`;
  }, [profileImageProp, user]);

  useEffect(() => {
    setImageError(false);
  }, [profileImage]);

  const initialCredits = useMemo(() => {
    if (creditsProp !== undefined && creditsProp !== null) {
      return Number(String(creditsProp).replace(/,/g, "")) || 0;
    }
    const stored = parseSessionJSON("CREDITS", null);
    return Number(stored ?? user.availableCredits ?? user.credits ?? 0) || 0;
  }, [creditsProp, user.availableCredits, user.credits]);
  const [creditBalance, setCreditBalance] = useState(initialCredits);
  const resolvedCredits = creditBalance.toLocaleString("en-AU");

  useEffect(() => {
    if (creditsProp !== undefined && creditsProp !== null) {
      setCreditBalance(Number(String(creditsProp).replace(/,/g, "")) || 0);
      return;
    }
    if (!user.businessUserID) return;
    getCachedRequest(
      `credits:${user.businessUserID}`,
      () =>
        axios.post(`${appConstants.baseURL}login/fetchCredit`, {
          businessUserID: user.businessUserID,
        }),
      30000
    )
      .then((response) => {
        const balance = Number(
          response.data?.Result_Output?.[0]?.availableCredits ?? 0
        );
        setCreditBalance(balance);
        sessionStorage.setItem("CREDITS", JSON.stringify(balance));
      })
      .catch(() => undefined);
  }, [creditsProp, user.businessUserID]);

  // Global search owns its query; page filters never feed into it.
  useEffect(() => {
    setLocalSearch("");
    setSearchResults([]);
    setSearchOpen(false);
    setMobileSearchOpen(false);
    setActiveResult(-1);
  }, [pathname]);

  useEffect(() => {
    const query = localSearch.trim();
    setSearchResults([]);
    setSearchError("");
    setActiveResult(-1);
    if (!query || !user.businessUserID) {
      setSearchLoading(false);
      return;
    }
    const rights = parseSessionJSON("ROLE_RIGHTS_LOGIN", []);
    const canSearch = (screen) =>
      Array.isArray(rights) &&
      rights.some(
        (right) =>
          right.screenName === screen &&
          ["Full Access", "View Only"].includes(right.screenRight)
      );
    const invoiceSource = {
      screen: "Invoices",
      kind: "Invoice",
      path: "/invoices/view/",
      field: "invoiceNumber",
      fields: [
        "invoiceNumber",
        "customerName",
        "customerCompanyName",
        "purchaseOrderNumber",
        "quotationNumber",
      ],
    };
    const sources = [
      { ...invoiceSource, endpoint: "invoice/fetchInvoices" },
      { ...invoiceSource, endpoint: "invoice/fetchDebts" },
      {
        screen: "Quotations",
        kind: "Quotation",
        endpoint: "quotation/fetchQuotations",
        path: "/quotations/view/",
        field: "quotationNumber",
        fields: [
          "quotationNumber",
          "customerName",
          "customerCompanyName",
          "purchaseOrderNumber",
        ],
      },
      {
        screen: "Contacts",
        kind: "Contact",
        endpoint: "contact/fetchContacts",
        path: "/contacts/editContact/",
        field: "companyName",
        fields: ["companyName", "name", "emailAddress"],
      },
    ].filter((source) => canSearch(source.screen));
    const abort = new AbortController();
    let cancelled = false;
    setSearchLoading(true);
    const timer = setTimeout(async () => {
      const token = sessionStorage.getItem("TOKEN") || user.token;
      // The existing list APIs accept regular expressions; search typed text literally.
      const filterValues = query.replace(/[.*+?^${}()|[\]\\]/g, (char) =>
        char === "+" ? "\\x2b" : "\\" + char
      );
      const responses = await Promise.allSettled(
        sources.map((source) =>
          axios.post(
            `${appConstants.baseURL}${source.endpoint}`,
            {
              businessUserID: user.businessUserID,
              token,
              page: 1,
              limit: 5,
              sortingType: "",
              sortingField: "",
              durationDate: "",
              fromDate: "",
              toDate: "",
              invoiceStatus: "All",
              activityStatus: "All",
              quotationStatus: "All",
              contactStatus: "All",
              contactType: "All",
              filterValues,
              searchParameterList: source.fields,
            },
            {
              headers: { Authorization: `Bearer ${token}` },
              signal: abort.signal,
              timeout: 10000,
            }
          )
        )
      );
      if (cancelled) return;
      const results = new Map();
      let failed = false;
      responses.forEach((response, index) => {
        if (
          response.status !== "fulfilled" ||
          !response.value.data?.Result_Status
        ) {
          failed = true;
          return;
        }
        const source = sources[index];
        const docs = response.value.data.Result_Output?.docs || [];
        docs.forEach((row) => {
          if (!row._id) return;
          const key = `${source.kind}:${row._id}`;
          results.set(key, {
            key,
            path: source.path + encodeURIComponent(row._id),
            kind: source.kind,
            label: row[source.field] || row.name || source.kind,
            description:
              source.kind === "Contact"
                ? row.emailAddress
                : row.customerCompanyName || row.customerName,
          });
        });
      });
      setSearchResults([...results.values()]);
      setSearchError(
        failed
          ? "Some search results could not be loaded. Please try again."
          : ""
      );
      setSearchLoading(false);
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      abort.abort();
    };
  }, [localSearch, user.businessUserID, user.token, searchRetry]);

  useEffect(() => {
    if (!searchOpen) return;
    const closeSearch = (event) => {
      if (!searchContainerRef.current?.contains(event.target))
        setSearchOpen(false);
    };
    document.addEventListener("mousedown", closeSearch);
    return () => document.removeEventListener("mousedown", closeSearch);
  }, [searchOpen]);

  const openSearchResult = (result) => {
    setSearchOpen(false);
    setLocalSearch("");
    navigate(result.path);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      setSearchOpen(false);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setSearchOpen(true);
      setActiveResult((current) =>
        searchResults.length
          ? current < 0
            ? event.key === "ArrowDown"
              ? 0
              : searchResults.length - 1
            : (current +
                (event.key === "ArrowDown" ? 1 : searchResults.length - 1)) %
              searchResults.length
          : -1
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (searchOpen && searchResults.length)
        openSearchResult(searchResults[activeResult < 0 ? 0 : activeResult]);
    }
  };

  // Close profile dropdown when clicking outside
  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (
        profileControlRef.current &&
        !profileControlRef.current.contains(e.target) &&
        profileMenuRef.current &&
        !profileMenuRef.current.contains(e.target)
      ) {
        setProfileOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [profileOpen]);

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setLocalSearch(val);
    setSearchResults([]);
    setSearchError("");
    setSearchLoading(Boolean(val.trim()));
    setSearchOpen(Boolean(val.trim()));
    setActiveResult(-1);
  };

  const handleRefresh = (event) => {
    const icon = event.currentTarget.querySelector("svg");
    if (
      !icon?.animate ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      window.location.reload();
      return;
    }
    icon.getAnimations().forEach((animation) => animation.cancel());
    const animation = icon.animate(
      [{ transform: "rotate(0deg)" }, { transform: "rotate(360deg)" }],
      { duration: 1000, easing: "linear" }
    );
    animation.onfinish = () => window.location.reload();
  };

  const handleHelpAction = () => {
    if (onHelpClick) {
      onHelpClick();
    } else {
      navigate("/help");
    }
  };

  const handleNotificationAction = () => {
    if (onNotificationClick) {
      onNotificationClick();
    } else {
      navigate(
        agreementNotifications.count
          ? `/rcti-agreements/${encodeURIComponent(
              agreementNotifications.latestID
            )}`
          : "/notifications"
      );
    }
  };

  const toggleMobileSearch = () => {
    setMobileSearchOpen((prev) => !prev);
    if (!mobileSearchOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  };

  return (
    <header
      className={`app-topbar dashboard-topbar ${
        darkMode ? "dark" : ""
      } ${className}`}
      data-theme={darkMode ? "dark" : "light"}
    >
      {/* Left section: mobile controls + desktop burger + search */}
      <div className="app-topbar__left">
        {/* Mobile drawer & search launcher (Phones < 768px) */}
        <div className="app-topbar__mobile-controls dashboard-mobile-controls">
          <button
            type="button"
            className="app-topbar__mobile-btn"
            onClick={
              onOpenNav ||
              (() =>
                window.dispatchEvent(
                  new CustomEvent("open-dashboard-navigation")
                ))
            }
            aria-label="Open navigation"
          >
            <LuMenu />
          </button>
          {showSearch && (
            <button
              type="button"
              className="app-topbar__mobile-btn"
              onClick={toggleMobileSearch}
              aria-label="Toggle search"
            >
              <SearchIcon />
            </button>
          )}
        </div>

        {/* Desktop burger button (visible on desktop when sidebar is collapsed) */}
        <button
          type="button"
          className={`app-topbar__desktop-burger${
            isSidebarClosed ? " is-visible" : ""
          }`}
          onClick={handleOpenDesktopSidebar}
          aria-label="Open navigation menu"
          title="Open sidebar"
        >
          <LuMenu />
        </button>

        {/* Global Search Bar (Figma Node I429:22166;534:24814) */}
        {showSearch && (
          <div
            ref={searchContainerRef}
            className={`app-topbar__search dashboard-search ${
              mobileSearchOpen ? "is-mobile-open" : ""
            }`}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget))
                setSearchOpen(false);
            }}
          >
            <div className="app-topbar__search-icon">
              <SearchIcon />
            </div>
            <input
              ref={searchInputRef}
              className="app-topbar__search-input"
              type="text"
              placeholder={searchPlaceholder}
              value={localSearch}
              onChange={handleSearchInput}
              onFocus={() => setSearchOpen(Boolean(localSearch.trim()))}
              onKeyDown={handleSearchKeyDown}
              role="combobox"
              aria-label="Global search"
              aria-autocomplete="list"
              aria-expanded={searchOpen && Boolean(localSearch.trim())}
              aria-controls="global-search-results"
              aria-activedescendant={
                searchOpen && activeResult >= 0
                  ? `global-search-result-${activeResult}`
                  : undefined
              }
              autoComplete="off"
            />
            {localSearch.length > 0 && (
              <button
                type="button"
                className="app-topbar__search-clear"
                aria-label="Clear search"
                title="Clear search"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setLocalSearch("");
                  setSearchResults([]);
                  setSearchError("");
                  setSearchLoading(false);
                  setSearchOpen(false);
                  setActiveResult(-1);
                  searchInputRef.current?.focus();
                }}
              >
                <LuX aria-hidden="true" />
              </button>
            )}
            {searchOpen && localSearch.trim() && (
              <div className="app-topbar__search-results">
                {searchLoading && <p role="status">Searching...</p>}
                {!searchLoading && searchError && (
                  <p role="alert">
                    {searchError}{" "}
                    <button
                      type="button"
                      onClick={() => setSearchRetry((value) => value + 1)}
                    >
                      Retry
                    </button>
                  </p>
                )}
                {!searchLoading && !searchError && !searchResults.length && (
                  <p role="status">No results found.</p>
                )}
                <div
                  id="global-search-results"
                  role="listbox"
                  aria-label="Global search results"
                >
                  {searchResults.map((result, index) => (
                    <button
                      type="button"
                      key={result.key}
                      id={`global-search-result-${index}`}
                      role="option"
                      aria-selected={index === activeResult}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => openSearchResult(result)}
                    >
                      <span>{result.kind}</span>
                      <strong>{result.label}</strong>
                      {result.description && (
                        <small>{result.description}</small>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right Actions Bar (Figma Node I429:22166;534:21917) */}
      <div className="app-topbar__actions dashboard-top-actions">
        {/* Credits Badge (Figma Node 158:5021) */}
        <button
          type="button"
          className="app-topbar__credits dashboard-topbar-credits"
          onClick={() => navigate("/settings?tab=billing&view=credits")}
          aria-label={`Available credits: ${resolvedCredits}`}
        >
          <div className="app-topbar__credits-icon">
            <CoinsIcon />
          </div>
          <span>Credits {resolvedCredits}</span>
        </button>

        <button
          type="button"
          className="app-topbar__icon-btn app-topbar__refresh-btn"
          onClick={handleRefresh}
          aria-label="Refresh page"
          data-tooltip="Refresh"
        >
          <LuRefreshCw aria-hidden="true" />
        </button>

        {/* Help Button (Figma Node 158:5025) */}
        <button
          type="button"
          className="app-topbar__icon-btn app-topbar__help-btn dashboard-topbar-help"
          onClick={handleHelpAction}
          aria-label="Help"
          data-tooltip="Help"
        >
          <InfoIcon />
        </button>

        {/* Notifications Button (Figma Node 158:5028) */}
        <button
          type="button"
          className="app-topbar__icon-btn app-topbar__notif-btn dashboard-notification"
          onClick={handleNotificationAction}
          aria-label="Notifications"
          data-tooltip="Notifications"
        >
          <BellIcon />
          {(notificationCount > 0 || agreementNotifications.count > 0) && (
            <span className="app-topbar__notif-badge" />
          )}
        </button>

        {/* Profile Control (Figma Node 422:17533) */}
        <button
          ref={profileControlRef}
          type="button"
          className={`app-topbar__profile-control dashboard-profile-control ${
            profileOpen ? "is-open" : ""
          }`}
          onClick={() => setProfileOpen((prev) => !prev)}
          aria-haspopup="menu"
          aria-expanded={profileOpen}
          aria-label="Open profile menu"
          data-tooltip="Profile"
        >
          <div className="app-topbar__avatar dashboard-avatar">
            {profileImage && !imageError ? (
              <img
                src={profileImage}
                alt={userName}
                crossOrigin="anonymous"
                onError={() => setImageError(true)}
              />
            ) : (
              <span>{getInitials(userName)}</span>
            )}
            <span className="app-topbar__status-dot" />
          </div>
          <div className="app-topbar__chevron">
            <ChevronDownIcon />
          </div>
        </button>
      </div>

      {/* Standard Profile Popover Menu */}
      {profileOpen && (
        <div
          ref={profileMenuRef}
          className="app-topbar__menu dashboard-profile-menu"
          role="menu"
        >
          <button
            type="button"
            className="app-topbar__menu-btn"
            onClick={() => {
              setProfileOpen(false);
              navigate("/settings?tab=profile");
            }}
          >
            <LuUser />
            <span>View profile</span>
          </button>

          <button
            type="button"
            className="app-topbar__menu-btn"
            onClick={() => {
              setProfileOpen(false);
              navigate("/settings?tab=business");
            }}
          >
            <LuSettings />
            <span>Account settings</span>
          </button>

          <div className="app-topbar__menu-divider" />

          <div className="app-topbar__menu-theme">
            <span>Appearance</span>
            <div className="app-topbar__theme-toggle">
              <button
                type="button"
                className={!darkMode ? "active" : ""}
                aria-pressed={!darkMode}
                onClick={() => setDarkMode(controllerDispatch, false)}
              >
                <LuSun />
                <span>Light</span>
              </button>
              <button
                type="button"
                className={darkMode ? "active" : ""}
                aria-pressed={darkMode}
                onClick={() => setDarkMode(controllerDispatch, true)}
              >
                <LuMoon />
                <span>Dark</span>
              </button>
            </div>
          </div>

          <button
            type="button"
            className="app-topbar__menu-btn"
            onClick={() => {
              clearAuthSession();
              window.location.replace("/login");
            }}
          >
            <LuLogOut />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </header>
  );
}

AppHeader.propTypes = {
  onOpenNav: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  credits: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  userName: PropTypes.string,
  profileImage: PropTypes.string,
  notificationCount: PropTypes.number,
  onNotificationClick: PropTypes.func,
  onHelpClick: PropTypes.func,
  className: PropTypes.string,
  showSearch: PropTypes.bool,
};
